import { useQuery } from '@apollo/client'
import type { SelectOption } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useCallback, useMemo } from 'react'
import type { SelectProps as BaseSelectProps } from './Select'
import { Select } from './Select'
import { ALL_SALARY_GROUPS } from '@/modules/salary/queries'
import type { SalaryGroupNode } from '@/modules/salary/types.graphql'
import { getNodes } from '@/utils/graphql'

interface SalaryGroupSelectProps
  extends Omit<BaseSelectProps<string, SalaryGroupNode>, 'options'> {
  alwaysInclude?: SalaryGroupNode
  isDiet?: boolean
  isSalaryDay?: boolean
  showInManualTimeEntryOvertime?: boolean
  isAddon?: boolean
  setOnLoad?: boolean
}

export const SalaryGroupSelect: React.FC<SalaryGroupSelectProps> = ({
  alwaysInclude = null,
  setOnLoad = false,

  // TODO unused?
  isDiet: _isDiet = undefined,
  isSalaryDay: _isSalaryDay = undefined,
  showInManualTimeEntryOvertime: _showInManualTimeEntryOvertime = undefined,
  isAddon: _isAddon = false,

  ...selectProps
}) => {
  const { placeholder } = useTranslate({
    placeholder: 'common.select-salary-group',
  })

  const { data, loading } = useQuery(ALL_SALARY_GROUPS, {
    onCompleted: data => {
      const groups = data.allSalaryGroups.edges.map(edge => edge?.node)
      const groupIds = groups.map(group => group?.id)
      if (setOnLoad && groupIds.length >= 1 && !selectProps.value) {
        selectProps.onChange(groupIds[0] ?? null, null)
      }
    },
  })

  const salaryGroups = getNodes(data?.allSalaryGroups)

  const getLabel = useCallback((salaryGroup?: SalaryGroupNode | null) => {
    return <span className="name">{salaryGroup?.name}</span>
  }, [])

  const options = useMemo<SelectOption<string, SalaryGroupNode>[]>(() => {
    if (!salaryGroups) return []

    const options = salaryGroups.map(salaryGroup => {
      return {
        value: salaryGroup.id,
        label: getLabel(salaryGroup),
        extra: salaryGroup,
      }
    })
    if (alwaysInclude === null) return options

    return [
      {
        value: alwaysInclude?.id ?? '',
        label: getLabel(alwaysInclude),
        extra: alwaysInclude,
      },
      ...options.filter(option => option.extra?.id !== alwaysInclude?.id),
    ]
  }, [alwaysInclude, getLabel, salaryGroups])

  return (
    <Select
      options={options}
      loading={loading}
      placeholder={placeholder}
      filterBackground="white"
      optionHoverBackground="white"
      {...selectProps}
    />
  )
}
